import { useEffect, useMemo, useState } from "react";

import BalanceIcon from "@mui/icons-material/Balance";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DownloadIcon from "@mui/icons-material/Download";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import GroupsIcon from "@mui/icons-material/Groups";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import FavoriteIcon from "@mui/icons-material/Favorite";
import PieChartIcon from "@mui/icons-material/PieChart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import type { Theme } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";

import { getMonthlyKPI, getYearlyOverview } from "@/api/kpi";
import { listOrganizations } from "@/api/organizations";
import { downloadMonthlyReportPdf, downloadYearlyReportPdf } from "@/api/reports";
import { useAuthStore } from "@/store/authStore";
import { CoverageMatrixCard } from "@/components/CoverageMatrixCard";
import { KpiCard } from "@/components/KpiCard";
import { PageHeader } from "@/components/PageHeader";

const PALETTE = ["#2563FF", "#22D3EE", "#6D5DFB", "#16A34A", "#F59E0B", "#EF4444", "#9333EA", "#475569"];

function monthLabel(year: number, month: number, locale: string): string {
  const date = new Date(year, month - 1, 1);
  return date.toLocaleString(locale, { month: "long", year: "numeric" });
}

function pct(v: number): string {
  return `${(v * 100).toFixed(1)} %`;
}

export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const me = useAuthStore((s) => s.me);

  const orgs = useQuery({
    queryKey: ["organizations-active"],
    queryFn: () => listOrganizations({ status: "ACTIVE", limit: 100 }),
  });

  const [orgId, setOrgId] = useState<string>("");
  useEffect(() => {
    if (!orgId && orgs.data?.items.length) {
      const preferred =
        me?.active_org_id && orgs.data.items.find((o) => o.id === me.active_org_id)
          ? me.active_org_id
          : orgs.data.items[0]?.id ?? "";
      setOrgId(preferred);
    }
  }, [orgs.data, orgId, me?.active_org_id]);

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);

  function navMonth(delta: number): void {
    const d = new Date(year, month - 1 + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  }

  const monthly = useQuery({
    queryKey: ["kpi-monthly", orgId, year, month],
    queryFn: () => getMonthlyKPI(orgId, year, month),
    enabled: Boolean(orgId),
  });

  const yearly = useQuery({
    queryKey: ["kpi-yearly", orgId, year],
    queryFn: () => getYearlyOverview(orgId, year),
    enabled: Boolean(orgId),
  });

  const [exporting, setExporting] = useState(false);
  const handleExportPdf = async () => {
    if (!orgId) return;
    setExporting(true);
    try {
      await downloadMonthlyReportPdf(orgId, year, month);
    } catch (_e) {
      alert(t("dashboard.exportError"));
    } finally {
      setExporting(false);
    }
  };

  const [exportingYearly, setExportingYearly] = useState(false);
  const handleExportYearlyPdf = async () => {
    if (!orgId) return;
    setExportingYearly(true);
    try {
      await downloadYearlyReportPdf(orgId, year);
    } catch (_e) {
      alert(t("dashboard.exportError"));
    } finally {
      setExportingYearly(false);
    }
  };

  if (orgs.isLoading) return <CircularProgress />;
  if ((orgs.data?.items.length ?? 0) === 0) {
    return (
      <Box>
        <PageHeader title={t("dashboard.title")} subtitle={t("dashboard.subtitle")} helpTerm="tableauBord" />
        <Alert severity="warning">{t("planning.noOrgAccessible")}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={t("dashboard.title")}
        subtitle={t("dashboard.subtitle")}
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportPdf}
              disabled={!orgId || exporting}
            >
              {exporting ? t("dashboard.exportingPdf") : t("dashboard.exportPdf")}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<DownloadIcon />}
              onClick={handleExportYearlyPdf}
              disabled={!orgId || exportingYearly}
            >
              {exportingYearly
                ? t("dashboard.exportingYearlyPdf")
                : t("dashboard.exportYearlyPdf", { year })}
            </Button>
          </>
        }
      />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        sx={{ mb: 3 }}
      >
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>{t("dashboard.selectOrganization")}</InputLabel>
          <Select
            label={t("dashboard.selectOrganization")}
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
          >
            {(orgs.data?.items ?? []).map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.code} — {o.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title={t("planning.previousMonth")}>
            <IconButton onClick={() => navMonth(-1)}>
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
          <Typography
            variant="h6"
            sx={{ minWidth: 200, textAlign: "center", textTransform: "capitalize" }}
          >
            {monthLabel(year, month, i18n.language)}
          </Typography>
          <Tooltip title={t("planning.nextMonth")}>
            <IconButton onClick={() => navMonth(1)}>
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {monthly.isLoading ? (
        <CircularProgress />
      ) : monthly.data ? (
        <MonthlyKpiView kpi={monthly.data} orgId={orgId} year={year} month={month} />
      ) : (
        <Alert severity="info">{t("dashboard.noData")}</Alert>
      )}

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          {t("dashboard.yearlyTitle")} {year}
        </Typography>
        {yearly.isLoading ? (
          <CircularProgress />
        ) : yearly.data ? (
          <YearlyView months={yearly.data.months} t={t} theme={theme} />
        ) : null}
      </Box>
    </Box>
  );
}


// ---------------------------------------------------------------------------
// Monthly view
// ---------------------------------------------------------------------------
function MonthlyKpiView({
  kpi,
  orgId,
  year,
  month,
}: {
  kpi: import("@/api/kpi").MonthlyKPI;
  orgId: string;
  year: number;
  month: number;
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  const coverageColor: "success" | "warning" | "error" =
    kpi.headcount_coverage_rate >= 0.95
      ? "success"
      : kpi.headcount_coverage_rate >= 0.7
        ? "warning"
        : "error";

  const totalHours = kpi.by_practitioner.reduce((s, p) => s + p.hours, 0);
  const wishRate = kpi.desiderata_wish_satisfaction_rate;
  const avoidRate = kpi.desiderata_avoid_respect_rate;

  const topPractitioners = kpi.by_practitioner.slice(0, 10);
  const modeData = kpi.by_exercise_mode.map((m, i) => ({
    name: m.code,
    value: m.hours,
    color: m.color ?? PALETTE[i % PALETTE.length] ?? "#888",
  }));
  const kindData = kpi.by_membership_kind.map((k, i) => ({
    name: k.kind,
    value: k.hours,
    color: PALETTE[i % PALETTE.length] ?? "#888",
  }));

  return (
    <Stack spacing={3}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label={t("dashboard.kpiCoverage")}
            value={pct(kpi.headcount_coverage_rate)}
            // Person-hours, not cells: "2 MRG expected, 1 present" is 50 %.
            hint={`${kpi.assigned_headcount} / ${kpi.required_headcount}`}
            color={coverageColor}
            progress={kpi.headcount_coverage_rate * 100}
            icon={<TrendingUpIcon />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label={t("dashboard.kpiCarences")}
            value={kpi.missing_headcount}
            hint={t("dashboard.kpiCarencesHint", {
              total: kpi.cells_empty,
              partial: kpi.cells_partial,
            })}
            color={kpi.missing_headcount === 0 ? "success" : "warning"}
            icon={<EventBusyIcon />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label={t("dashboard.kpiGini")}
            value={kpi.gini_weighted_hours.toFixed(3)}
            hint={t("dashboard.giniNote")}
            color={
              kpi.gini_weighted_hours <= 0.15
                ? "success"
                : kpi.gini_weighted_hours <= 0.3
                  ? "warning"
                  : "error"
            }
            icon={<BalanceIcon />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label={t("dashboard.kpiTotalHours")}
            value={totalHours.toFixed(0)}
            hint={`${kpi.by_practitioner.length} praticiens`}
            color="info"
            icon={<PieChartIcon />}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label={t("dashboard.kpiWishSat")}
            value={wishRate !== null ? pct(wishRate) : "—"}
            hint={`${kpi.desiderata_wishes_satisfied} / ${kpi.desiderata_wishes_total}`}
            color={wishRate === null ? "default" : wishRate >= 0.7 ? "success" : "warning"}
            icon={<FavoriteIcon />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label={t("dashboard.kpiAvoidRespect")}
            value={avoidRate !== null ? pct(avoidRate) : "—"}
            hint={`${kpi.desiderata_avoids_violated} violés / ${kpi.desiderata_avoids_total}`}
            color={avoidRate === null ? "default" : avoidRate >= 0.7 ? "success" : "warning"}
            icon={<ThumbDownAltOutlinedIcon />}
          />
        </Grid>

        {/* --- Activity indicators -------------------------------------- */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label={t("dashboard.kpiRegulationDays")}
            // Effectif / théorique. Les deux côte à côte : l'écart est le
            // nombre de jours d'ouverture restés sans aucune affectation.
            value={`${kpi.regulation_days} / ${kpi.operating_days}`}
            hint={
              kpi.operating_days > kpi.regulation_days
                ? t("dashboard.kpiRegulationDaysGap", {
                    count: kpi.operating_days - kpi.regulation_days,
                  })
                : t("dashboard.kpiRegulationDaysHint", { days: kpi.operating_days })
            }
            color="info"
            icon={<EventAvailableIcon />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label={t("dashboard.kpiContributors")}
            value={kpi.contributors}
            hint={t("dashboard.kpiContributorsHint")}
            color="info"
            icon={<GroupsIcon />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label={t("dashboard.kpiRemoteShare")}
            value={kpi.remote_rate !== null ? pct(kpi.remote_rate) : "—"}
            hint={t("dashboard.kpiRemoteShareHint", {
              remote: kpi.hours_remote.toFixed(0),
              onsite: kpi.hours_onsite.toFixed(0),
            })}
            color="default"
            icon={<HomeWorkIcon />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            label={t("dashboard.kpiLiberalShare")}
            value={kpi.liberal_rate !== null ? pct(kpi.liberal_rate) : "—"}
            hint={t("dashboard.kpiLiberalShareHint", {
              liberal: kpi.hours_liberal.toFixed(0),
              contract: kpi.hours_contract.toFixed(0),
            })}
            color="default"
            icon={<BusinessCenterIcon />}
          />
        </Grid>
      </Grid>

      <CoverageMatrixCard organizationId={orgId} year={year} month={month} />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 7 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                {t("dashboard.chartTopPractitioners")}
              </Typography>
              {topPractitioners.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t("dashboard.noPractitionerData")}
                </Typography>
              ) : (
                <Box sx={{ height: 360 }}>
                  <ResponsiveContainer>
                    <BarChart
                      data={topPractitioners}
                      layout="vertical"
                      margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                      <XAxis type="number" stroke={theme.palette.text.secondary} />
                      <YAxis
                        type="category"
                        dataKey="display_name"
                        width={140}
                        stroke={theme.palette.text.secondary}
                      />
                      <RTooltip
                        contentStyle={{
                          backgroundColor: theme.palette.background.paper,
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      />
                      <Bar dataKey="weighted_hours" fill={theme.palette.primary.main} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 2.5 }}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {t("dashboard.chartByMode")}
              </Typography>
              <DonutChart data={modeData} theme={theme} />
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, lg: 2.5 }}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {t("dashboard.chartByKind")}
              </Typography>
              <DonutChart data={kindData} theme={theme} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
}


function DonutChart({
  data,
  theme,
}: {
  data: { name: string; value: number; color: string }[];
  theme: Theme;
}) {
  const total = data.reduce((s, x) => s + x.value, 0);
  if (total <= 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Box sx={{ height: 240 }}>
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            innerRadius={50}
            outerRadius={80}
            paddingAngle={2}
          >
            {data.map((entry, i) => (
              <Cell key={i} fill={entry.color} />
            ))}
          </Pie>
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <RTooltip
            formatter={(v) => `${Number(v).toFixed(1)} h`}
            contentStyle={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
}


// ---------------------------------------------------------------------------
// Yearly view
// ---------------------------------------------------------------------------
type YearlyMonth = import("@/api/kpi").YearlyMonthEntry;

function YearlyView({
  months,
  t,
  theme,
}: {
  months: YearlyMonth[];
  t: (k: string) => string;
  theme: Theme;
}) {
  const data = useMemo(() => {
    return months.map((m) => ({
      month: `${m.month.toString().padStart(2, "0")}`,
      coverage_pct: m.total_slots > 0 ? (m.coverage_rate * 100).toFixed(1) : 0,
      gini: m.gini_weighted_hours,
      wish: m.wish_satisfaction_rate !== null ? m.wish_satisfaction_rate * 100 : null,
    }));
  }, [months]);

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, lg: 6 }}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              {t("dashboard.chartCoverageYear")}
            </Typography>
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis dataKey="month" stroke={theme.palette.text.secondary} />
                  <YAxis stroke={theme.palette.text.secondary} unit="%" />
                  <RTooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  />
                  <Bar dataKey="coverage_pct" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, lg: 6 }}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              {t("dashboard.chartGiniYear")}
            </Typography>
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer>
                <LineChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis dataKey="month" stroke={theme.palette.text.secondary} />
                  <YAxis stroke={theme.palette.text.secondary} domain={[0, 1]} />
                  <RTooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="gini"
                    stroke="#6D5DFB"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
